import React, { useState, useEffect } from 'react';
import { Download, Eye, Calendar, DollarSign, Loader } from 'lucide-react';
import { subscriptionService } from '../../services/subscriptionService';
import { formatCurrency, formatDate } from '../../utils/helpers';

const BillingHistoryTable = ({ onViewInvoice }) => {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadBillingHistory();
  }, []);

  const loadBillingHistory = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await subscriptionService.getBillingHistory();
      setInvoices(response.invoices || []);
    } catch (err) {
      console.error('Error loading billing history:', err);
      setError('Failed to load billing history');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      paid: 'bg-green-100 text-green-800',
      open: 'bg-yellow-100 text-yellow-800',
      void: 'bg-gray-100 text-gray-800',
      uncollectible: 'bg-red-100 text-red-800'
    };
    
    return badges[status] || 'bg-gray-100 text-gray-800';
  };

  const getStatusLabel = (status) => {
    const labels = {
      paid: 'Paid',
      open: 'Pending',
      void: 'Void',
      uncollectible: 'Failed'
    };
    
    return labels[status] || status;
  };

  const handleDownload = (e, invoice) => {
    e.stopPropagation();
    if (invoice.pdfUrl) {
      window.open(invoice.pdfUrl, '_blank');
    }
  };

  const handleView = (invoice) => {
    if (onViewInvoice) {
      onViewInvoice(invoice);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader className="h-8 w-8 text-blue-600 animate-spin" />
        <span className="ml-3 text-gray-600">Loading billing history...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-center">
        <p className="text-red-800">{error}</p>
        <button
          onClick={loadBillingHistory}
          className="mt-3 text-blue-600 hover:text-blue-700 font-medium text-sm"
        >
          Try Again
        </button>
      </div>
    );
  }

  if (invoices.length === 0) {
    return (
      <div className="text-center py-12 border-2 border-dashed border-gray-300 rounded-lg">
        <DollarSign className="h-12 w-12 text-gray-400 mx-auto mb-3" />
        <p className="text-gray-600">No billing history yet</p>
        <p className="text-sm text-gray-500 mt-1">Your invoices will appear here</p>
      </div>
    );
  }

  return (
    <div>
      {/* Desktop Table - Hidden on mobile */}
      <div className="hidden md:block overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-gray-200 bg-gray-50">
              <th className="text-left py-3 px-4 font-semibold text-sm text-gray-700">Date</th>
              <th className="text-left py-3 px-4 font-semibold text-sm text-gray-700">Invoice</th>
              <th className="text-left py-3 px-4 font-semibold text-sm text-gray-700">Description</th>
              <th className="text-right py-3 px-4 font-semibold text-sm text-gray-700">Amount</th>
              <th className="text-center py-3 px-4 font-semibold text-sm text-gray-700">Status</th>
              <th className="text-center py-3 px-4 font-semibold text-sm text-gray-700">Actions</th>
            </tr>
          </thead>
          <tbody>
            {invoices.map((invoice) => (
              <tr 
                key={invoice.invoiceId} 
                className="border-b border-gray-100 hover:bg-gray-50 transition-colors cursor-pointer"
                onClick={() => handleView(invoice)}
              >
                <td className="py-4 px-4">
                  <div className="flex items-center gap-2 text-sm text-gray-900">
                    <Calendar size={16} className="text-gray-400" />
                    {formatDate(invoice.date)}
                  </div>
                </td>
                <td className="py-4 px-4">
                  <span className="text-sm font-mono text-gray-900">
                    {invoice.invoiceNumber || invoice.invoiceId.substring(0, 8)}
                  </span>
                </td>
                <td className="py-4 px-4">
                  <span className="text-sm text-gray-700">
                    {invoice.description}
                  </span>
                </td>
                <td className="py-4 px-4 text-right">
                  <span className="text-sm font-semibold text-gray-900">
                    {formatCurrency(invoice.amount)}
                  </span>
                </td>
                <td className="py-4 px-4 text-center">
                  <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${getStatusBadge(invoice.status)}`}>
                    {getStatusLabel(invoice.status)}
                  </span>
                </td>
                <td className="py-4 px-4">
                  <div className="flex items-center justify-center gap-2">
                    <button
                      onClick={(e) => { e.stopPropagation(); handleView(invoice); }}
                      className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                      title="View Invoice"
                    >
                      <Eye size={16} />
                    </button>
                    {invoice.pdfUrl && (
                      <button
                        onClick={(e) => handleDownload(e, invoice)}
                        className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                        title="Download PDF"
                      >
                        <Download size={16} />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile Card View - Only shown on mobile */}
      <div className="md:hidden space-y-3">
        {invoices.map((invoice) => (
          <div 
            key={invoice.invoiceId} 
            className="bg-white border border-gray-200 rounded-lg p-4 cursor-pointer active:bg-gray-50 transition-colors"
            onClick={() => handleView(invoice)}
          >
            {/* Header row: Invoice # and Status */}
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm font-mono text-gray-600">
                #{invoice.invoiceNumber || invoice.invoiceId.substring(0, 8)}
              </span>
              <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getStatusBadge(invoice.status)}`}>
                {getStatusLabel(invoice.status)}
              </span>
            </div>
            
            {/* Main content: Description and Amount */}
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-gray-900 font-medium flex-1 mr-4">
                {invoice.description}
              </span>
              <span className="text-lg font-bold text-gray-900">
                {formatCurrency(invoice.amount)}
              </span>
            </div>

            {/* Footer: Date and Actions */}
            <div className="flex items-center justify-between pt-3 border-t border-gray-100">
              <div className="flex items-center gap-1.5 text-xs text-gray-500">
                <Calendar size={12} />
                {formatDate(invoice.date)}
              </div>
              <div className="flex gap-2">
                <button
                  onClick={(e) => { e.stopPropagation(); handleView(invoice); }}
                  className="px-3 py-1.5 text-xs text-blue-600 border border-blue-200 rounded-lg hover:bg-blue-50 transition-colors flex items-center gap-1"
                >
                  <Eye size={12} />
                  View
                </button>
                {invoice.pdfUrl && (
                  <button
                    onClick={(e) => handleDownload(e, invoice)}
                    className="px-3 py-1.5 text-xs text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors flex items-center gap-1"
                  >
                    <Download size={12} />
                    PDF
                  </button>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default BillingHistoryTable;